import { useSelector as useReduxSelector } from 'react-redux'

import { IAppState } from 'models/store.model'

type TSelector<T> = (state: IAppState) => T
type TEqualityFn<T> = (previousValue: T, nextValue: T) => boolean

type TSelectorConfig<T> = {
  select: TSelector<T>
  equalityFn: TEqualityFn<T>
}

type TSelectorMap<TSelectedValues> = {
  [K in keyof TSelectedValues]:
    | TSelector<TSelectedValues[K]>
    | TSelectorConfig<TSelectedValues[K]>
}

const toSelectorConfig = <T>(
  selectorOrConfig: TSelector<T> | TSelectorConfig<T>,
): TSelectorConfig<T> =>
  typeof selectorOrConfig === 'function'
    ? { select: selectorOrConfig, equalityFn: Object.is }
    : selectorOrConfig

export const useBulkSelector = <TSelectedValues extends Record<string, any>>(
  selectors: TSelectorMap<TSelectedValues>,
): TSelectedValues => {
  const selectorConfigs = Object.entries(selectors).map(
    ([selectorKey, selectorOrConfig]) =>
      [selectorKey, toSelectorConfig(selectorOrConfig)] as const,
  )

  return useReduxSelector(
    (state: IAppState) =>
      Object.fromEntries(
        selectorConfigs.map(([selectorKey, { select }]) => [
          selectorKey,
          select(state),
        ]),
      ) as TSelectedValues,
    (previousValues, nextValues) =>
      selectorConfigs.every(([selectorKey, { equalityFn }]) =>
        equalityFn(previousValues[selectorKey], nextValues[selectorKey]),
      ),
  )
}
